import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Trophy, Shield, Users, Target, HelpCircle, Mail, Clock, Phone, Ticket } from "lucide-react";
import { ALL_BETTING_SITES } from "@/lib/bettingSites";
import WonProofsSection from "@/components/WonProofsSection";

const Footer = () => {
  const [currentTime, setCurrentTime] = useState("");
  const [timezone, setTimezone] = useState(() => localStorage.getItem("fixedraws_tz") || Intl.DateTimeFormat().resolvedOptions().timeZone);

  useEffect(() => {
    const update = () => {
      try {
        const now = new Date();
        setCurrentTime(now.toLocaleTimeString("en-US", { timeZone: timezone, hour: "2-digit", minute: "2-digit", hour12: true }));
      } catch { setCurrentTime(new Date().toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: true })); }
    };
    update();
    const i = setInterval(update, 30000);
    return () => clearInterval(i);
  }, [timezone]);

  const handleTzChange = (e) => { setTimezone(e.target.value); localStorage.setItem("fixedraws_tz", e.target.value); };

  return (
    <>
      <WonProofsSection />
      <footer className="bg-white border-t border-gray-200">
      <div className="max-w-7xl mx-auto px-4">
        {/* Time Bar */}
        <div className="py-3 border-b border-gray-100 flex flex-wrap items-center justify-center gap-3 text-sm">
          <div className="flex items-center gap-2 text-gray-600">
            <Clock className="h-4 w-4 text-green-600" />
            <span className="font-medium" data-testid="footer-time">{currentTime}</span>
            <select value={timezone} onChange={handleTzChange} className="bg-gray-50 border border-gray-200 rounded-md px-2 py-1 text-xs text-gray-600 cursor-pointer" data-testid="timezone-select">
              <option value="Africa/Lagos">WAT (Lagos)</option>
              <option value="Africa/Accra">GMT (Accra)</option>
              <option value="Africa/Nairobi">EAT (Nairobi)</option>
              <option value="Africa/Johannesburg">SAST (Johannesburg)</option>
              <option value="Africa/Cairo">EET (Cairo)</option>
              <option value="Europe/London">GMT (London)</option>
              <option value="America/New_York">EST (New York)</option>
              <option value="UTC">UTC</option>
            </select>
          </div>
        </div>

        {/* Main footer */}
        <div className="py-10 grid grid-cols-1 md:grid-cols-4 gap-8">
          <div>
            <Link to="/" className="flex items-center space-x-2 mb-4">
              <Trophy className="h-7 w-7 text-green-600" />
              <span className="text-xl font-bold logo-text">FIXEDRAWS</span>
            </Link>
            <p className="text-sm text-gray-500">Expert fixed draw predictions for football pools. Trusted by thousands of punters worldwide.</p>
          </div>
          <div>
            <h4 className="font-semibold mb-4 text-gray-900">Quick Links</h4>
            <ul className="space-y-2 text-sm text-gray-500">
              <li><Link to="/" className="hover:text-green-600 transition-colors">Home</Link></li>
              <li><Link to="/buy" className="hover:text-green-600 transition-colors">Buy Draws</Link></li>
              <li><Link to="/pool/draws" className="hover:text-green-600 transition-colors">Pool Draws</Link></li>
              <li><Link to="/pool/results" className="hover:text-green-600 transition-colors">Pool Results</Link></li>
              <li><Link to="/view-code" className="hover:text-green-600 transition-colors">View Predictions</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="font-semibold mb-4 text-gray-900">Support</h4>
            <ul className="space-y-2 text-sm text-gray-500">
              <li><Link to="/support" className="hover:text-green-600 transition-colors flex items-center gap-1.5" data-testid="footer-support-ticket"><HelpCircle className="h-3.5 w-3.5" /> Open Support Ticket</Link></li>
              <li><Link to="/contact" className="hover:text-green-600 transition-colors flex items-center gap-1.5" data-testid="footer-contact"><Mail className="h-3.5 w-3.5" /> Contact us</Link></li>
              <li><a href="mailto:support@fixedraws.com" className="hover:text-green-600 transition-colors flex items-center gap-1.5" data-testid="footer-email"><Mail className="h-3.5 w-3.5" /> support@fixedraws.com</a></li>
            </ul>
          </div>
          <div>
            <h4 className="font-semibold mb-4 text-gray-900">We Accept</h4>
            <div className="grid grid-cols-3 gap-2">
              {[
                { name: "BTC", color: "bg-orange-100 text-orange-600" },
                { name: "ETH", color: "bg-blue-100 text-blue-600" },
                { name: "USDT", color: "bg-green-100 text-green-600" },
                { name: "SOL", color: "bg-purple-100 text-purple-600" },
                { name: "BNB", color: "bg-yellow-100 text-yellow-700" },
                { name: "XRP", color: "bg-slate-100 text-slate-600" }
              ].map((c) => (
                <div key={c.name} className={`${c.color} rounded-md px-2 py-1 text-xs font-bold text-center`}>{c.name}</div>
              ))}
            </div>
            <div className="mt-2 space-y-2">
              <div className="bg-indigo-50 text-indigo-700 rounded-md px-2 py-1 text-xs font-medium text-center">Bank Transfer (NG)</div>
              <div className="grid grid-cols-2 gap-2">
                <div className="bg-indigo-50 text-indigo-600 rounded-md px-2 py-1 text-[11px] font-medium text-center">Steam Card</div>
                <div className="bg-emerald-50 text-emerald-700 rounded-md px-2 py-1 text-[11px] font-medium text-center">Razer Gold</div>
              </div>
            </div>
          </div>
        </div>

        {/* Betting Partners */}
        <div className="py-6 border-t border-gray-100">
          <div className="flex items-center gap-2 mb-3">
            <Ticket className="h-4 w-4 text-green-600" />
            <h4 className="font-semibold text-gray-900 text-sm">Booking Codes Available For</h4>
          </div>
          <div className="flex flex-wrap gap-2">
            {ALL_BETTING_SITES.map(s => (
              <span key={s.code} className="inline-flex items-center gap-1.5 text-xs font-bold rounded-full px-3 py-1" style={{ backgroundColor: s.color + "12", color: s.color }}>
                <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: s.color }} />
                {s.name}
              </span>
            ))}
          </div>
        </div>

        {/* Responsible Gambling */}
        <div className="bg-gray-50 border-t border-gray-200 -mx-4 px-4">
          <div className="max-w-7xl mx-auto py-6">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 rounded-full bg-red-500 flex items-center justify-center text-white font-bold text-lg border-2 border-white shadow">18+</div>
                <div className="text-sm">
                  <p className="font-semibold text-gray-900">Adults Only</p>
                  <p className="text-gray-500">This website is for users aged 18+</p>
                </div>
              </div>
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 rounded-full bg-emerald-500 flex items-center justify-center text-white border-2 border-white shadow">
                  <Mail className="h-5 w-5" />
                </div>
                <div className="text-sm">
                  <p className="font-semibold text-gray-900">Customer Support</p>
                  <p className="text-gray-500">Open a <Link to="/support" className="text-green-600 font-semibold">support ticket</Link> or email <a href="mailto:support@fixedraws.com" className="text-green-600 font-semibold">support@fixedraws.com</a></p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 rounded-lg bg-green-50 flex items-center justify-center" title="Gamble Responsibly"><Shield className="h-5 w-5 text-green-600" /></div>
                <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center" title="Self Exclusion"><Users className="h-5 w-5 text-blue-600" /></div>
                <div className="w-10 h-10 rounded-lg bg-purple-50 flex items-center justify-center" title="Secure"><Target className="h-5 w-5 text-purple-600" /></div>
              </div>
            </div>
          </div>
        </div>

        {/* Managed by Madam Precious Draws */}
        <div className="py-4 border-t border-gray-200 bg-green-50/50 -mx-6 px-6 mt-4" data-testid="footer-managed-by">
          <p className="text-center text-sm text-gray-700">
            This website is managed by <strong className="text-green-700">Madam Precious Draws</strong> in regards to football (pool) predictions.
          </p>
        </div>

        {/* Bottom bar */}
        <div className="py-4 border-t border-gray-200">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-gray-500">
            <p>&copy; {new Date().getFullYear()} Fixedraws. All rights reserved.</p>
            <p className="text-center text-gray-500">Gambling can be addictive. Please play responsibly. Customer support: <Link to="/support" className="text-green-600 font-semibold">open a ticket</Link></p>
            <div className="flex items-center space-x-4">
              <Link to="/terms" className="hover:text-gray-600 text-gray-500" data-testid="footer-terms">Terms</Link>
              <Link to="/privacy" className="hover:text-gray-600 text-gray-500" data-testid="footer-privacy">Privacy</Link>
              <Link to="/cookies" className="hover:text-gray-600 text-gray-500" data-testid="footer-cookies">Cookies</Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
    </>
  );
};

export default Footer;
